import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@/lib/utils";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn("flex overflow-x-auto border-b border-line", className)}
    {...props}
  />
));
TabsList.displayName = "TabsList";

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & { count?: number; countMuted?: boolean }
>(({ className, count, countMuted, children, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "group inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-4 py-2.5 text-sm font-semibold text-muted outline-none transition-colors data-[state=active]:border-primary data-[state=active]:text-primary hover:text-ink focus-visible:ring-2 focus-visible:ring-primary-soft",
      className
    )}
    {...props}
  >
    {children}
    {/* The badge is only rendered for a usable number. `count` is typed
        `number | undefined`, but a count derived from partly-loaded data can
        still arrive as `null` or `NaN`, and the previous `count !== undefined`
        guard rendered those as an empty badge (blank, taking up space and
        saying nothing) or as the literal text "NaN" — a fabricated figure.
        Omitting the badge is how pages already express an unknown count:
        `count={loading ? undefined : rows.length}`. */}
    {typeof count === "number" && Number.isFinite(count) && (
      <span className={cn("text-xs font-semibold", countMuted ? "text-subtle" : "text-primary group-data-[state=inactive]:text-muted")}>
        {count}
      </span>
    )}
  </TabsPrimitive.Trigger>
));
TabsTrigger.displayName = "TabsTrigger";

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn("data-[state=inactive]:hidden", className)}
    {...props}
  />
));
TabsContent.displayName = "TabsContent";

export { Tabs, TabsList, TabsTrigger, TabsContent };